import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useGetPropertyDetail } from 'dataQueries'
import { usePatchPropertyDetail } from 'dataQueries/property.query'
import type { CartItem } from 'models/purchaseFlowRevamp'
import type { PropertyType } from 'models/property/realEstatePropertyDTO'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { logger } from 'utils/logging'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { usePfEffectiveWorkspaceId } from '../../entry/usePfEffectiveWorkspaceId'
import type { FloorPlanConfigDraftDTO } from './floorPlanConfig.dto'

/** The Residential / Commercial answer of the cart's first floor-plan configuration that has one. */
export const floorPlanPropertyUsage = (
  items: ReadonlyArray<Pick<CartItem, 'id'>>,
  productConfigs: Record<string, unknown>
): PropertyType | null => {
  for (const item of items) {
    const config = productConfigs[makeConfigKey(item.id, 'floor_plan_config')] as FloorPlanConfigDraftDTO | undefined
    if (config?.selectedPropertyType) return config.selectedPropertyType
  }
  return null
}

/**
 * Writes the floor-plan configuration's Residential / Commercial answer onto the order's property. The v2 order
 * carries no property type, and the floor-plan editor's instructions read it from the property, which the flow no
 * longer asks for at creation.
 */
export function useFloorPlanPropertyUsageSync(): void {
  const usage = usePfStore((s) => floorPlanPropertyUsage(s.items, s.productConfigs))
  const propertyId = usePfStore((s) => s.property?.id ?? null)
  const workspaceId = usePfEffectiveWorkspaceId()
  const detail = useGetPropertyDetail(propertyId, workspaceId || null, true, { asClient: true })
  const storedUsage = detail.data?.propertyType
  const { mutate } = usePatchPropertyDetail({ asClient: true })
  const queryClient = useQueryClient()
  const sent = useRef<string | null>(null)

  useEffect(() => {
    if (!usage || !propertyId || !workspaceId || !detail.data || storedUsage === usage) return
    const attempt = `${propertyId}:${usage}`
    if (sent.current === attempt) return
    sent.current = attempt
    mutate(
      { workspaceId, propertyId, payload: { propertyType: usage, propertyUsage: usage } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [EntityKeys.PROPERTY, QueryType.GET_ONE] })
        },
        onError: (error) => {
          sent.current = null
          logger.warn('pf-revamp could not save the floor-plan property type on the property', error, {
            context: { component: 'useFloorPlanPropertyUsageSync', action: 'patchProperty', metadata: { propertyId, usage } },
          })
        },
      }
    )
  }, [usage, propertyId, workspaceId, detail.data, storedUsage, mutate, queryClient])
}
